/**
 * useUserDetails Hook
 *
 * Feature-specific hook for fetching and managing user details.
 * Part of clean architecture: Component → Hook → UseCase → Service
 *
 * Pattern:
 * - Encapsulates business logic for user details feature
 * - Uses React Query for caching and background updates
 * - Returns only what the UI needs
 * - Prevents unnecessary re-renders with proper dependency management
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '@/presentation/store';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { createFetchUserDetailsUseCase } from '@/domain/usecases/user/fetchUserDetailsUseCase';
import { getUserByUsernameUseCase } from '@/domain/usecases/user/getUserByUsernameUseCase';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { ExclusiveContentSubscriptionStatus } from '@/data/enums/exclusiveSubscriptions/exclusiveContentSubscriptionStatus';
import { selectExclusiveContentSubscriptionStatus } from '@/presentation/store/slices/exclusiveContent/exclusiveContentSlice';

export interface UseUserDetailsResult {
  userData: UserEntity | null;
  isLoading: boolean;
  error: Error | null;
  /** The resolved Firebase user ID (same as input userId, or resolved from mention) */
  resolvedUserId: string | undefined;
  /** True when the resolved profile belongs to the authenticated viewer. */
  isCurrentUser: boolean;
  /**
   * Subscribe-button label that reflects the live exclusive-content subscription
   * status with the target creator. Mirrors Flutter's user_details_screen.dart:983-1006:
   *   - active    → 'Unsubscribe'
   *   - requested → 'Cancel request'
   *   - inactive / cancelled → 'Subscribe'
   *   - own profile → 'Account'
   */
  subscribeLabel: string;
  fetchUser: () => Promise<void>;
}

/**
 * Hook for fetching user details by Firebase ID or by mention/username.
 *
 * Supports two flows (matching Flutter's user_details_screen.dart):
 * - By userId: Fetches user directly by Firebase ID
 * - By mention: Resolves username to user via Firestore query, then exposes resolvedUserId
 *
 * @param params - Either { userId } or { mention } to identify the user
 * @param cacheKey - Cache key for React Query (screenKey or pathname for routes without screenKey)
 * @returns User details state, resolvedUserId, and actions
 */
export const useUserDetails = (
  params: { userId?: string; mention?: string },
  cacheKey: string[],
): UseUserDetailsResult => {
  const queryClient = useQueryClient();
  const { l10n } = useLocale();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [resolvedUserId, setResolvedUserId] = useState<string | undefined>(params.userId);
  const isLoadingRef = useRef(false);

  // Get cached data reactively from React Query cache
  const userData = queryClient.getQueryData<UserEntity>(cacheKey) ?? null;

  // Own-profile flag — exposed so callers can gate per-profile fetches like useUserMedias.
  const isCurrentUser = !!currentUser && !!resolvedUserId && currentUser.firebaseId === resolvedUserId;

  // Live exclusive-content subscription status with the target creator. The
  // ExclusiveContentListener mirrors Firestore into Redux, so this re-renders
  // whenever the viewer subscribes/unsubscribes. Falls back to '' (which the
  // selector resolves to `inactive`) while resolvedUserId is still loading,
  // matching Flutter's "no subscription doc" path.
  const subscriptionStatus = useSelector((state: RootState) =>
    selectExclusiveContentSubscriptionStatus(state, resolvedUserId ?? ''),
  );
  const subscribeLabel = useMemo(() => {
    if (isCurrentUser) return l10n.userDetails.account;
    switch (subscriptionStatus) {
      case ExclusiveContentSubscriptionStatus.active:
        return l10n.common.unsubscribe;
      case ExclusiveContentSubscriptionStatus.requested:
        return l10n.common.cancelRequest;
      case ExclusiveContentSubscriptionStatus.inactive:
      case ExclusiveContentSubscriptionStatus.cancelled:
      default:
        return l10n.common.subscribe;
    }
  }, [isCurrentUser, subscriptionStatus, l10n]);

  // Keep ref in sync with state
  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  /**
   * Manually fetch user details
   * - If userId is available: fetches by Firebase ID (existing behavior)
   * - If only mention is available: resolves username to user, sets resolvedUserId
   */
  const fetchUser = useCallback(async () => {
    // Check if data already exists in cache
    const cachedData = queryClient.getQueryData(cacheKey);
    if (cachedData) {
      // Data exists in cache, don't refetch (for POP navigation)
      return;
    }

    // Prevent duplicate fetches
    if (isLoadingRef.current) {
      return;
    }

    // No cache exists, fetch fresh data (for PUSH navigation)
    setIsLoading(true);
    isLoadingRef.current = true;
    setError(null);

    try {
      if (params.userId) {
        // Fetch by Firebase ID (existing flow)
        await queryClient.fetchQuery({
          queryKey: cacheKey,
          queryFn: async () => {
            const useCase = createFetchUserDetailsUseCase();
            const user = await useCase.execute(params.userId!);
            if (!user) {
              throw new Error(`User not found: ${params.userId}`);
            }
            return user;
          },
          staleTime: Infinity,
          gcTime: Infinity,
        });
      } else if (params.mention) {
        // Fetch by username/mention (new flow - matches Flutter's getUserByUsername)
        const userDto = await getUserByUsernameUseCase.execute(params.mention);
        if (!userDto) {
          throw new Error('User not found');
        }
        // UserDto and UserEntity are identical structures, cast safely
        const userEntity = userDto as unknown as UserEntity;
        // Cache the user data
        queryClient.setQueryData(cacheKey, userEntity);
        // Expose the resolved Firebase ID for media fetching
        setResolvedUserId(userEntity.firebaseId);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch user details'));
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [params.userId, params.mention, cacheKey, queryClient]);

  return {
    userData,
    isLoading,
    error,
    resolvedUserId,
    isCurrentUser,
    subscribeLabel,
    fetchUser,
  };
};
